/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import Stack from "../components/ui/Stack";
import Title from "../components/ui/Title";
import TimeSlotPicker from "../components/TimeSlotPicker";
import LoadingIndicator from "../components/LoadingIndicator";
import RescheduleBookingSummary from "../components/booking/RescheduleBookingSummary";
import CannotRescheduleEmptyState from "../components/booking/CannotRescheduleEmptyState";
import { ROUTES } from "../constants/constants";
import { useBooking } from "../hooks/useBooking";
import { useBookingApiEntities } from "../hooks/useBookingApiEntities";
import { useGetBooking } from "../lib/react-query/useGetBooking";
import { useGetBookings } from "../lib/react-query/useGetBookings";
import { useRescheduleBooking } from "../lib/react-query/useRescheduleBooking";
import { useToast } from "../components/ui/Toast";
import useWidgetCallbackParams from "../hooks/useWidgetCallbackParams";
import { computeBatchSpan } from "../utils/helpers/computeBatchSpan";

const RescheduleBooking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { setBookingResponse } = useBooking();
  const { hash, clearParams } = useWidgetCallbackParams();
  const [date, setDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [pendingTime, setPendingTime] = useState(null);

  useEffect(() => {
    clearParams();
  }, [clearParams]);

  const { includeEntities } = useBookingApiEntities("booking");
  const { data: booking, isLoading: isLoadingBooking } = useGetBooking(
    id,
    { hash, include: includeEntities },
    { throwOnError: true, retry: false },
  );

  const { mutateAsync: reschedule, isPending: isRescheduling } = useRescheduleBooking(id);

  // The loaded booking exposes is_sequential/batch_id, so for a sequential batch
  // fetch its siblings to show the full span in the summary. Client-scoped endpoint,
  // so this resolves for logged-in clients; guests fall back to the single value.
  const isSequentialBatch = Boolean(booking?.is_sequential && booking?.batch_id);

  const { data: batchData } = useGetBookings(
    {
      "filter[batch_id]": booking?.batch_id,
      "filter[batch_type]": booking?.batch_type,
    },
    { enabled: isSequentialBatch },
  );

  const batchInfo = useMemo(
    () => (batchData?.data ? computeBatchSpan(batchData.data) : null),
    [batchData],
  );

  const backTarget = location.state?.from || ROUTES.HOME;

  const handleDateChange = (next) => {
    setDate(next instanceof Date ? format(next, "yyyy-MM-dd") : next);
  };

  const handleSlotClick = async (time) => {
    if (isRescheduling) return;
    setPendingTime(time);

    try {
      const response = await reschedule({ start_datetime: `${date} ${time}`, hash });

      // BookingStatus's data hook expects the create-flow shape `{ bookings: [...] }`.
      // Reschedule returns a single booking entity, or an array for a sequential batch.
      const rescheduled = Array.isArray(response) ? response : [response];
      setBookingResponse({ bookings: rescheduled.map(({ id }) => ({ id })) });
      navigate(ROUTES.BOOKING_RESCHEDULED);
    } catch (err) {
      toast({
        title: "Error",
        description: err?.response?.data?.message || "you_cannot_reschedule_this_booking",
        variant: "error",
      });
      setPendingTime(null);
    }
  };

  if (isLoadingBooking || !booking) {
    return (
      <Container>
        <NavigationHeader title="Reschedule Booking" back={backTarget} />
        <LoadingIndicator page />
      </Container>
    );
  }

  const service = booking.service;
  const services = service ? [service] : [];

  return (
    <Container>
      <NavigationHeader title="Reschedule Booking" back={backTarget} />
      <Stack as="section" $gap="1.5rem">
        <RescheduleBookingSummary booking={booking} batchInfo={batchInfo} />
        {booking.can_reschedule_booking ? (
          <>
            <Title size="h3">Select Date & Time</Title>
            <TimeSlotPicker
              date={date}
              onDateChange={handleDateChange}
              bookingId={booking.id}
              services={services}
              provider={booking.provider ?? null}
              location={booking.location}
              pendingTime={pendingTime}
              onSlotClick={handleSlotClick}
              disabled={isRescheduling}
            />
          </>
        ) : (
          <CannotRescheduleEmptyState reason={booking.can_reschedule_booking_error_reason} />
        )}
      </Stack>
    </Container>
  );
};

export default RescheduleBooking;
